import { useMutation, useQueryClient } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { IUIUserFavoriteModel } from "../../services/ui-personalization-data-service/Models";
import UIPersonalizationDataService from "../../services/ui-personalization-data-service/UIPersonalizationDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { FavoriteMutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseDeleteUserFavoriteResultType = {
    deletedDescription: string;
};

export type UseDeleteUserFavoriteParameters = {
} & BaseUseMutationParameters<UseDeleteUserFavoriteResultType, IUIUserFavoriteModel>;

export function useDeleteUserFavorite({
    onError,
    onSuccess
}: UseDeleteUserFavoriteParameters) {
    const queryClient = useQueryClient();

    return useMutation<UseDeleteUserFavoriteResultType, RestError, IUIUserFavoriteModel, FavoriteMutationContextType>(
        async (favorite) => {
            await UIPersonalizationDataService.deleteUserFavorite(favorite.id);

            return {
                deletedDescription: favorite.description
            };
        },
        {
            onMutate: async (favorite: IUIUserFavoriteModel) => {

                await queryClient.cancelQueries(keys.all);

                const previousFavorites = queryClient.getQueryData<Array<IUIUserFavoriteModel>>(
                    keys.favoriteList()
                );
                
                queryClient.setQueryData<Array<IUIUserFavoriteModel>>(
                    keys.favoriteList(),
                    (favorites: Array<IUIUserFavoriteModel> | undefined) => {

                        return favorites?.filter(r => r.id !== favorite.id) ?? []
                    }
                );

                return {
                    previousFavorites: previousFavorites
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _favorite, context) => {

                if (error && context?.previousFavorites) {
                    queryClient.setQueryData<Array<IUIUserFavoriteModel>>(
                        keys.favoriteList(),
                        context?.previousFavorites
                    );
                }

                if (!error) {
                    queryClient.invalidateQueries(keys.favoriteList());
                }
            }
        }
    );
}
